// src/components/dashboard/AllProducts.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { EditIcon, Trash, TrashWhite } from "../../assets/icons/icons";
// import Pagination from "../section/Pagination"; // لم نستخدمه الآن لأننا جهزنا ترقيم داخلي
import axios from "axios";
import CustomAlert from "../CustomAlert";

const API_BASE_URL = "https://laravel-api.aj-mart.com/api";
const STORAGE_BASE = "https://laravel-api.aj-mart.com/storage/app/public/";

const imgUrl = (p) => (p ? `${STORAGE_BASE}${p}` : "");
const money = (v) => {
  const n = Number.parseFloat(v ?? 0);
  return Number.isFinite(n) ? n.toFixed(3) : "0.000";
};
const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

// Safely normalize fields that may come in multiple shapes
const getColors = (p) =>
  Array.isArray(p?.product_color_array)
    ? p.product_color_array
    : Array.isArray(p?.product_color)
    ? p.product_color
    : [];

const getColorHexes = (p) =>
  Array.isArray(p?.product_colors_with_hex) ? p.product_colors_with_hex : [];

const getCapacities = (p) =>
  Array.isArray(p?.product_capacity_array)
    ? p.product_capacity_array
    : Array.isArray(p?.product_capacity)
    ? p.product_capacity
    : [];

const getCapacityPrices = (p) => {
  const raw = p?.product_capacity_prices;
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      const obj = JSON.parse(raw);
      return isObj(obj) ? obj : {};
    } catch {
      return {};
    }
  }
  return isObj(raw) ? raw : {};
};

/* ===== axios anti-cache defaults (حماية إضافية) ===== */
try {
  axios.defaults.headers.get = {
    ...(axios.defaults.headers.get || {}),
    "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  };
} catch {}

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set()); // multiple expand
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });

  // Dialogs
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  // لتجميد الصف أثناء الحذف + منع ضغط أزرار مكررة
  const [deletingIds, setDeletingIds] = useState([]);

  // ===== Pagination state =====
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

  useEffect(() => {
    fetchProducts(true); // أول جلب مع cache-buster لضمان fresh
  }, []);

  const fetchProducts = async (bust = false) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/products`, {
        params: { _ts: bust ? Date.now() : undefined },
        headers: {
          "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        withCredentials: true,
      });

      const payload = response.data?.products ?? response.data?.data?.products;
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      setProducts(list);
      setError(null);
      setCurrentPage(1); // ارجع لأول صفحة بعد الجلب
    } catch (err) {
      setError("Failed to fetch products");
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  const totalItems = products.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // تأكد أن currentPage ضمن المدى بعد أي تغيير بالبيانات
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
    if (currentPage < 1) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: "", message: "" }), 5000);
  };

  const handleDelete = (productId) => {
    setDeleteItemId(productId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    const id = deleteItemId;
    setShowDeleteDialog(false);
    if (!id) return;

    setDeletingIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    const prev = products;
    setProducts((cur) => cur.filter((p) => p.product_id !== id));
    setSelectedProducts((cur) => cur.filter((x) => x !== id));
    setExpanded((prevEx) => {
      const n = new Set(prevEx);
      n.delete(id);
      return n;
    });

    try {
      await axios.delete(`${API_BASE_URL}/products/delete/${id}`);
      showAlert("success", "Product deleted successfully");
      await fetchProducts(true);
    } catch (err) {
      console.error("Error deleting product:", err);
      setProducts(prev);
      showAlert("error", err?.response?.data?.message || "Failed to delete product");
    } finally {
      setDeletingIds((prev) => prev.filter((x) => x !== id));
      setDeleteItemId(null);
    }
  };

  const handleBulkDelete = () => {
    if (!selectedProducts.length) {
      showAlert("error", "Please select products to delete");
      return;
    }
    setShowBulkDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    const ids = [...selectedProducts];
    setShowBulkDeleteDialog(false);
    if (!ids.length) return;

    setDeletingIds((prev) => [...new Set([...prev, ...ids])]);
    const prev = products;
    setProducts((cur) => cur.filter((p) => !ids.includes(p.product_id)));
    setSelectedProducts([]);
    setExpanded(new Set());

    try {
      const results = await Promise.allSettled(
        ids.map((productId) => axios.delete(`${API_BASE_URL}/products/delete/${productId}`))
      );
      const failed = results.filter(
        (r) => r.status === "rejected" || (r.value && r.value.data && r.value.data.status === false)
      );
      if (failed.length) {
        showAlert("error", "Some deletions failed. Refreshing…");
        await fetchProducts(true);
      } else {
        showAlert("success", "Selected products deleted successfully");
        await fetchProducts(true);
      }
    } catch (err) {
      console.error("Error deleting products:", err);
      showAlert("error", err?.response?.data?.message || "Failed to delete selected products");
      setProducts(prev);
    } finally {
      setDeletingIds((prev) => prev.filter((x) => !ids.includes(x)));
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  // اختَر فقط العناصر الظاهرة في الصفحة الحالية
  const handleSelectAllVisible = (e) => {
    const checked = e.target.checked;
    const visibleIds = paginatedRows.map((p) => p.product_id);
    setSelectedProducts((prev) => {
      if (checked) {
        // أضف IDs الظاهرة الآن + احتفظ بالمختارة سابقًا
        return Array.from(new Set([...prev, ...visibleIds]));
      } else {
        // احذف IDs الظاهرة الآن من المختارة
        return prev.filter((id) => !visibleIds.includes(id));
      }
    });
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const rows = useMemo(() => products, [products]);

  // ==== بيانات الصفحة الحالية ====
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paginatedRows = useMemo(() => rows.slice(startIdx, endIdx), [rows, startIdx, endIdx]);

  // هل كل عناصر الصفحة الحالية مختارة؟
  const allVisibleChecked =
    paginatedRows.length > 0 &&
    paginatedRows.every((p) => selectedProducts.includes(p.product_id));

  if (loading) return <div className="py-[25px]">Loading products...</div>;
  if (error) return <div className="py-[25px] text-red-500">{error}</div>;

  return (
    <section className="py-[25px]">
      {alert.show && (
        <CustomAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ show: false, type: "", message: "" })}
        />
      )}

      <div className="main-title mb-[20px]">All Products</div>

      <div className="flex flex-wrap justify-between items-center gap-[10px] capitalize mb-[20px]">
        <div className="text-sm text-gray-600">
          Showing{" "}
          <span className="font-semibold">
            {totalItems === 0 ? 0 : startIdx + 1}–{Math.min(endIdx, totalItems)}
          </span>{" "}
          of <span className="font-semibold">{totalItems}</span> items
        </div>

        <button
          className={`btn bg-red-500 hover:bg-red-600 flex items-center gap-2 ${
            !selectedProducts.length ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={handleBulkDelete}
          disabled={!selectedProducts.length}
        >
          <TrashWhite />
          <span>Delete Products ({selectedProducts.length})</span>
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-200 text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-200 p-[10px]">
                <input
                  type="checkbox"
                  onChange={handleSelectAllVisible}
                  checked={allVisibleChecked}
                />
              </th>
              <th className="border border-gray-200 p-[10px]">IMG</th>
              <th className="border border-gray-200 p-[10px]">Product Name</th>
              <th className="border border-gray-200 p-[10px]">Category / Sub</th>
              <th className="border border-gray-200 p-[10px]">Brand</th>
              <th className="border border-gray-200 p-[10px]">ID</th>
              <th className="border border-gray-200 p-[10px]">Stock</th>
              <th className="border border-gray-200 p-[10px]">Price</th>
              <th className="border border-gray-200 p-[10px]">Discount</th>
              <th className="border border-gray-200 p-[10px]">Status</th>
              <th className="border border-gray-200 p-[10px]">Tools</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((product) => {
              const colors = getColors(product);
              const colorsHex = getColorHexes(product);
              const caps = getCapacities(product);
              const capPrices = getCapacityPrices(product);
              const imgs =
                (Array.isArray(product.product_images_array) ? product.product_images_array : product.product_images) ||
                [];

              const isDeleting = deletingIds.includes(product.product_id);
              const checked = selectedProducts.includes(product.product_id);

              return (
                <React.Fragment key={product.product_id}>
                  <tr className={`bg-white hover:bg-gray-50 align-top ${isDeleting ? "opacity-60" : ""}`}>
                    <td className="border border-gray-200 p-[10px]">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleSelectProduct(product.product_id)}
                        disabled={isDeleting}
                      />
                    </td>
                    <td className="border border-gray-200 p-[10px]">
                      <img
                        src={imgUrl(product.product_main_img)}
                        alt={product.product_name_E}
                        className="w-16 h-16 rounded object-cover bg-white border"
                        onError={(e) => {
                          e.currentTarget.src =
                            "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64'><rect width='100%' height='100%' fill='%23eee'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-size='10' fill='%23999'>No Image</text></svg>";
                        }}
                      />
                    </td>
                    <td className="border border-gray-200 p-[10px]">
                      <div className="font-semibold">{product.product_name_E}</div>
                      {product.product_name_A && (
                        <div className="text-xs text-gray-600">{product.product_name_A}</div>
                      )}
                    </td>
                    <td className="border border-gray-200 p-[10px]">
                      <div>{product.product_category || "—"}</div>
                      <div className="text-xs text-gray-600">{product.product_sub_category || "—"}</div>
                    </td>
                    <td className="border border-gray-200 p-[10px]">{product.product_brand_name || "—"}</td>
                    <td className="border border-gray-200 p-[10px]">{product.product_id}</td>
                    <td className="border border-gray-200 p-[10px]">{product.product_stock_available ?? "—"}</td>
                    <td className="border border-gray-200 p-[10px]">{money(product.product_price)}</td>
                    <td className="border border-gray-200 p-[10px]">
                      {product.product_price_discount ? money(product.product_price_discount) : "—"}
                    </td>
                    <td className="border border-gray-200 p-[10px]">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                          (product.product_status || "").toLowerCase() === "new"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {product.product_status || "—"}
                      </span>
                    </td>
                    <td className="border border-gray-200 p-[10px]">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="px-2 py-1 rounded border text-xs hover:bg-gray-50"
                          onClick={() => toggleExpand(product.product_id)}
                          title="View details"
                          disabled={isDeleting}
                        >
                          {expanded.has(product.product_id) ? "Hide" : "Details"}
                        </button>
                        <Link
                          to={`/editProduct/${product.product_id}`}
                          className={`cursor-pointer ${isDeleting ? "pointer-events-none opacity-50" : ""}`}
                          title="Edit"
                        >
                          <EditIcon />
                        </Link>
                        <button
                          className={`cursor-pointer ${isDeleting ? "pointer-events-none opacity-50" : ""}`}
                          onClick={() => handleDelete(product.product_id)}
                          title="Delete"
                          disabled={isDeleting}
                        >
                          <Trash />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {expanded.has(product.product_id) && (
                    <tr className="bg-white">
                      <td className="border border-gray-200 p-[10px]" colSpan={11}>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {/* Colors */}
                          <div className="p-3 border rounded-lg">
                            <div className="font-semibold mb-2">Colors</div>
                            {getColors(product).length === 0 && !getColorHexes(product).length ? (
                              <div className="text-sm text-gray-500">—</div>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {getColorHexes(product).length > 0
                                  ? getColorHexes(product).map((c, idx) => {
                                      const name = c?.name ?? c?.color_name ?? getColors(product)[idx] ?? "Color";
                                      const hex = (c?.hex ?? c?.color_hex ?? "").toString().replace(/^#?/, "#");
                                      return (
                                        <span
                                          key={`${name}-${idx}`}
                                          className="inline-flex items-center gap-2 px-2 py-1 rounded-full border bg-white"
                                          title={name}
                                        >
                                          <span
                                            className="inline-block w-4 h-4 rounded-full border"
                                            style={{ backgroundColor: hex || "#fff" }}
                                          />
                                          <span className="text-xs">{name}</span>
                                        </span>
                                      );
                                    })
                                  : getColors(product).map((name, idx) => (
                                      <span
                                        key={`${name}-${idx}`}
                                        className="inline-flex items-center gap-2 px-2 py-1 rounded-full border bg-white"
                                      >
                                        <span className="inline-block w-4 h-4 rounded-full border bg-gray-100" />
                                        <span className="text-xs">{name}</span>
                                      </span>
                                    ))}
                              </div>
                            )}
                          </div>

                          {/* Capacities & Prices */}
                          <div className="p-3 border rounded-lg">
                            <div className="font-semibold mb-2">Capacities & Prices</div>
                            {getCapacities(product).length === 0 && Object.keys(getCapacityPrices(product)).length === 0 ? (
                              <div className="text-sm text-gray-500">—</div>
                            ) : (
                              <div className="space-y-1">
                                {getCapacities(product).length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {getCapacities(product).map((c, idx) => (
                                      <span
                                        key={`${c}-${idx}`}
                                        className="px-2 py-1 rounded-full border bg-white text-xs"
                                      >
                                        {c}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {Object.keys(getCapacityPrices(product)).length > 0 && (
                                  <ul className="list-disc list-inside text-sm text-gray-700">
                                    {Object.entries(getCapacityPrices(product)).map(([cap, price]) => (
                                      <li key={cap}>
                                        <span className="font-medium">{cap}:</span> {money(price)}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Images */}
                          <div className="p-3 border rounded-lg">
                            <div className="font-semibold mb-2">Gallery</div>
                            {(Array.isArray(product.product_images_array) ? product.product_images_array : product.product_images || []).length === 0 ? (
                              <div className="text-sm text-gray-500">—</div>
                            ) : (
                              <div className="grid grid-cols-3 gap-2">
                                {(Array.isArray(product.product_images_array) ? product.product_images_array : product.product_images).map((p, idx) => (
                                  <img
                                    key={idx}
                                    src={imgUrl(p)}
                                    alt={`img-${idx}`}
                                    className="w-full h-20 object-cover rounded border"
                                  />
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Tags & Features */}
                          <div className="p-3 border rounded-lg lg:col-span-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <div className="font-semibold mb-1">Tags</div>
                                {product.product_tag ? (
                                  <div className="flex flex-wrap gap-2">
                                    {String(product.product_tag)
                                      .split(",")
                                      .map((t) => t.trim())
                                      .filter(Boolean)
                                      .map((t, i) => (
                                        <span
                                          key={`${t}-${i}`}
                                          className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs border"
                                        >
                                          {t}
                                        </span>
                                      ))}
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-500">—</div>
                                )}
                              </div>
                              <div>
                                <div className="font-semibold mb-1">Stock</div>
                                <div className="text-sm">
                                  {product.product_stock_available ?? "—"}
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                              <div>
                                <div className="font-semibold mb-1">Features (EN)</div>
                                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {product.product_features_E || "—"}
                                </div>
                              </div>
                              <div>
                                <div className="font-semibold mb-1">Features (AR)</div>
                                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {product.product_features_A || "—"}
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                              <div>
                                <div className="font-semibold mb-1">Description (EN)</div>
                                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {product.product_desc_E || "—"}
                                </div>
                              </div>
                              <div>
                                <div className="font-semibold mb-1">Description (AR)</div>
                                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {product.product_desc_A || "—"}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Meta */}
                          <div className="p-3 border rounded-lg">
                            <div className="font-semibold mb-2">Meta</div>
                            <ul className="text-sm text-gray-700 space-y-1">
                              <li>
                                <span className="font-medium">Created:</span>{" "}
                                {product.created_at
                                  ? new Date(product.created_at).toLocaleString()
                                  : "—"}
                              </li>
                              <li>
                                <span className="font-medium">Updated:</span>{" "}
                                {product.updated_at
                                  ? new Date(product.updated_at).toLocaleString()
                                  : "—"}
                              </li>
                              <li>
                                <span className="font-medium">Brand ID:</span>{" "}
                                {product.brand_id ?? "—"}
                              </li>
                              <li>
                                <span className="font-medium">Category ID:</span>{" "}
                                {product.category_id ?? "—"}
                              </li>
                              <li>
                                <span className="font-medium">Sub Category ID:</span>{" "}
                                {product.sub_category_id ?? "—"}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ====== Pagination Controls ====== */}
      <div className="py-[20px] flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-gray-600">
          Page <span className="font-semibold">{currentPage}</span> of{" "}
          <span className="font-semibold">{totalPages}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            « First
          </button>
          <button
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ‹ Prev
          </button>

          {/* أزرار مرقمة مختصرة حول الصفحة الحالية */}
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => {
                // أظهر النافذة: [currentPage-2, currentPage+2] + أول وآخر صفحة
                return (
                  p === 1 ||
                  p === totalPages ||
                  (p >= currentPage - 2 && p <= currentPage + 2)
                );
              })
              .reduce((acc, p, idx, arr) => {
                // أضف "…" عند الفجوات
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("gap");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "gap" ? (
                  <span key={`gap-${idx}`} className="px-2 text-gray-500">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`px-3 py-1.5 rounded border ${
                      currentPage === p ? "bg-gray-800 text-white border-gray-800" : "hover:bg-gray-50"
                    }`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
          </div>

          <button
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next ›
          </button>
          <button
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            Last »
          </button>
        </div>
      </div>

      {/* Single Delete Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-5 text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this product?</p>
            <div className="flex justify-center gap-4">
              <button
                className="btn bg-gray-300 hover:bg-gray-400"
                onClick={() => setShowDeleteDialog(false)}
              >
                No
              </button>
              <button className="btn bg-red-500 hover:bg-red-600" onClick={confirmDelete}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Dialog */}
      {showBulkDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-5 text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Bulk Deletion</h2>
            <p className="mb-4">
              Are you sure you want to delete {selectedProducts.length} products?
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="btn bg-gray-300 hover:bg-gray-400"
                onClick={() => setShowBulkDeleteDialog(false)}
              >
                Cancel
              </button>
              <button className="btn bg-red-500 hover:bg-red-600" onClick={confirmBulkDelete}>
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AllProducts;
